import { jsx as _jsx } from "react/jsx-runtime";
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useTheme } from '@mui/material';
import { Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import { forwardRef } from 'react';
import { DATETIME_DD_MM_YYYY_24H } from '@application/utilities/date-formats';
dayjs.extend(updateLocale);
dayjs.updateLocale('en', { weekStart: 1 });
export const CustomDatePicker = forwardRef(({ label, control, name, placeholder = '', width = '100%', rules, disabled = false, error, variant = false, bg = 'light' }, ref) => {
    const theme = useTheme();
    // handy tokens
    const primaryDark = theme.palette.primary.darker;
    const primaryDarker = theme.palette.primary.darker ?? theme.palette.primary.dark;
    const popupPaperSx = {
        backgroundColor: variant ? '#2E3440' : 'white',
        color: variant ? theme.palette.common.white : theme.palette.text.primary,
        boxShadow: variant ? '-20px 20px 40px -4px #919EAB3D, 0px 0px 2px 0px #919EAB3D' : 'none',
        /* Tabs (Date / Time) */
        '& .MuiTabs-root': {
            minHeight: 44
        },
        '& .MuiTab-root:not(.Mui-selected)': {
            color: bg === 'grey8' ? '#454F5B' : undefined
        },
        '& .MuiTab-root.Mui-selected': {
            color: primaryDark
        },
        '& .MuiTabs-indicator': {
            backgroundColor: primaryDark
        },
        /* Action bar buttons */
        '& .MuiPickersLayout-actionBar .MuiButton-root:first-of-type': {
            border: `1px solid ${primaryDark}`,
            color: primaryDark,
            backgroundColor: 'transparent'
        },
        '& .MuiPickersLayout-actionBar .MuiButton-root:last-of-type': {
            color: theme.palette.common.white,
            backgroundColor: primaryDarker
        },
        '& .MuiPickersLayout-actionBar .MuiButton-root:first-of-type:hover': {
            backgroundColor: theme.palette.action.hover
        },
        /* Calendar days */
        '& .MuiPickersDay-root.Mui-selected': {
            backgroundColor: primaryDark,
            color: theme.palette.primary.contrastText
        },
        '& .MuiPickersDay-root.MuiPickersDay-today:not(.Mui-selected)': {
            /* today (when not selected) */
            backgroundColor: 'transparent',
            color: theme.palette.text.primary
        },
        /* Clock colors */
        '& .MuiClockPointer-root': {
            backgroundColor: primaryDarker
        },
        '& .MuiClockPointer-thumb': {
            borderColor: primaryDarker,
            backgroundColor: primaryDarker
        },
        '& .MuiClock-pin': {
            backgroundColor: primaryDarker
        }
    };
    return (_jsx(Controller, { name: name, control: control, rules: rules, render: ({ field }) => (_jsx(DateTimePicker, { desktopModeMediaQuery: '@media(min-width:9999px)', value: field.value ? dayjs(field.value) : null, onChange: (date) => field.onChange(date ? date.toDate() : null), disabled: disabled, ampm: false, format: DATETIME_DD_MM_YYYY_24H, slotProps: {
                textField: {
                    inputRef: ref,
                    label,
                    placeholder,
                    variant: 'filled',
                    fullWidth: true,
                    error: !!error,
                    helperText: error,
                    disabled,
                    sx: {
                        width,
                        border: 'none',
                        '& .MuiFilledInput-root': {
                            backgroundColor: variant
                                ? '#2E3440'
                                : bg === 'grey8' || bg === 'grey8Event'
                                    ? theme.palette.common.grey8
                                    : undefined,
                            color: variant
                                ? 'white'
                                : bg === 'grey8' || bg === 'grey8Event'
                                    ? theme.palette.grey[700]
                                    : undefined,
                            borderRadius: bg === 'grey8Event' ? '12px' : bg === 'grey8' ? 2 : undefined,
                            boxShadow: bg === 'grey8' || bg === 'grey8Event' ? 'none' : undefined,
                            '&:hover': {
                                backgroundColor: variant
                                    ? '#2E3440'
                                    : bg === 'grey8' || bg === 'grey8Event'
                                        ? theme.palette.common.grey8
                                        : undefined
                            },
                            '&.Mui-focused': {
                                backgroundColor: variant
                                    ? '#2E3440'
                                    : bg === 'grey8' || bg === 'grey8Event'
                                        ? theme.palette.common.grey8
                                        : undefined,
                                border: bg === 'grey8' || bg === 'grey8Event'
                                    ? `1px solid ${theme.palette.grey[400]}`
                                    : undefined
                            },
                            '&:before, &:after': {
                                display: bg === 'grey8' || bg === 'grey8Event' ? 'none' : undefined
                            }
                        },
                        '& .MuiInputLabel-root': {
                            color: bg === 'grey8' || bg === 'grey8Event'
                                ? theme.palette.grey[500]
                                : variant
                                    ? 'white'
                                    : undefined
                        }
                    },
                    InputProps: { disableUnderline: true }
                },
                /* Desktop popper */
                popper: {
                    sx: {
                        '& .MuiPaper-root': popupPaperSx
                    }
                },
                /* Mobile dialog paper */
                mobilePaper: {
                    sx: popupPaperSx
                },
                /* Picker open button (keep as-is) */
                openPickerButton: {
                    sx: {
                        border: 'none',
                        '&:hover': { backgroundColor: 'transparent' }
                    }
                }
            } })) }));
});
